import { useMemo, useState } from "react"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { ActiveUsinagesCard } from "@/features/mill-floor/components/active-usinages-card"
import { PastUsinagesCard } from "@/features/mill-floor/components/past-usinages-card"
import { WarehouseCard } from "@/features/mill-floor/components/warehouse-card"
import {
  loadRemainingBags,
  loadRemainingKg,
  qdlRemainingBags,
} from "@/features/mill-floor/lib/load-math"
import {
  useMillFloorLoads,
  useMillFloorQueueLots,
  useMillFloorUsinages,
} from "@/features/mill-floor/hooks/use-mill-floor"
import { ScheduleUsinageDialog } from "@/features/milling/components/schedule-usinage-dialog"
import { formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

export function MillFloorPage() {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const [scheduleOpen, setScheduleOpen] = useState(false)

  const { data: loads, isLoading: loadingLoads } = useMillFloorLoads("all")
  const { data: queueLots } = useMillFloorQueueLots("all")
  const { data: activeRuns, isLoading: loadingActive } =
    useMillFloorUsinages("active")
  const { data: historyRuns, isLoading: loadingHistory } =
    useMillFloorUsinages("history")

  const allLoads = useMemo(() => loads ?? [], [loads])
  const allQueueLots = useMemo(() => queueLots ?? [], [queueLots])
  const activeList = activeRuns ?? []
  const historyList = historyRuns ?? []

  const { availableCount, warehouseMT, availableQdlCount } = useMemo(() => {
    let count = 0
    let kg = 0
    for (const l of allLoads) {
      if (l.derivedStatus !== "available") continue
      if (loadRemainingBags(l) <= 0) continue
      count += 1
      kg += loadRemainingKg(l)
    }
    let qdlCount = 0
    for (const q of allQueueLots) {
      if (q.derivedStatus !== "available") continue
      if (qdlRemainingBags(q) <= 0) continue
      qdlCount += 1
    }
    return {
      availableCount: count,
      warehouseMT: kgToMt(kg),
      availableQdlCount: qdlCount,
    }
  }, [allLoads, allQueueLots])

  const canSchedule = availableCount > 0 || availableQdlCount > 0

  const subtitle =
    activeList.length > 0
      ? t("millFloor.subtitleActive", {
          runs: activeList.length,
          ready: availableCount,
          mt: formatNumber(warehouseMT, 1),
        })
      : t("millFloor.subtitleIdle", {
          ready: availableCount,
          mt: formatNumber(warehouseMT, 1),
        })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t("millFloor.eyebrow")}
        title={t("millFloor.title")}
        description={subtitle}
        actions={
          <Button
            size="sm"
            onClick={() => setScheduleOpen(true)}
            disabled={!canSchedule}
          >
            <Plus className="size-4" />
            {t("millFloor.schedule")}
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <WarehouseCard
          loads={allLoads}
          queueLots={allQueueLots}
          isLoading={loadingLoads}
        />
        <ActiveUsinagesCard
          runs={activeList}
          isLoading={loadingActive}
          onSchedule={() => setScheduleOpen(true)}
          scheduleDisabled={!canSchedule}
        />
      </div>

      {!loadingHistory && historyList.length > 0 ? (
        <PastUsinagesCard runs={historyList} />
      ) : null}

      <ScheduleUsinageDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        availableLoads={allLoads.filter(
          (l) =>
            l.derivedStatus === "available" && loadRemainingBags(l) > 0,
        )}
        availableQueueLots={allQueueLots.filter(
          (q) =>
            q.derivedStatus === "available" && qdlRemainingBags(q) > 0,
        )}
        onCreated={(usinageId) => {
          setScheduleOpen(false)
          navigate(paths.usinageDetail(usinageId))
        }}
      />
    </div>
  )
}
